import { TBiometricConfidenceLevel, TDocumentConfidenceLevel } from './types';

/**
 * The statement prose that follows the bold level name on a check card, one sentence (or a short
 * paragraph) per level: what happened, transcribed character for character from the `statement`
 * field of the frozen prototype's copy table.
 *
 * **Not the whole of the prototype's copy table.** The prototype carries a second, optional field
 * per level, `guidance` (what the client should do next), rendered in its own tinted box. This file
 * used to be named `guidance.ts` and to claim it held that table transcribed whole; it did not, and
 * the name is what let the two fields be read as one. `guidanceCopy.ts` holds the `guidance` field
 * that this file was never carrying. See verifime-adr#264.
 *
 * **Deliberately excludes the level name itself.** The card renders the name in bold, then this
 * prose straight after in the same paragraph, and the name already has a home in
 * `documentConfidenceLevelName`/`biometricConfidenceLevelName` (see `ladder.ts`). Storing the name a
 * second time in here would give the two tables a chance to drift apart on a level neither owns.
 *
 * `High` and `VeryHigh` on the document axis read almost identically, save a plurality shift:
 * `High` says "document ... its", `VeryHigh` says "documents ... their". That tracks the level, not
 * a document count the card has no prop for, so the two strings are kept distinct rather than
 * templated from one, which would need a count this component does not and should not have.
 */
const DOCUMENT_STATEMENT: Record<TDocumentConfidenceLevel, string> = {
  NotCompleted:
    'Your customer’s identity documents are currently being checked. No conclusions have been made yet.',
  Low: 'Low level of confidence in this person’s identity documents.',
  Substantial:
    'The identity document was read digitally and its format, structure and security features were checked against our library of known document templates for this document type. The document is consistent with a genuine document. The document’s details have not been confirmed against the issuing authority’s records.',
  High: 'The identity document was read digitally and its format, structure and security features were checked against our library of known document templates for this document type. The document is consistent with a genuine document. The details on the document were also confirmed against an independent source.',
  VeryHigh:
    'The identity documents were read digitally and their format, structure and security features were checked against our library of known document templates for these document types. The documents are consistent with genuine documents. The details on the documents were also confirmed against an independent source.',
};

/**
 * `InProgress` reads as a short, deliberately noncommittal sentence: the check ran and there is
 * nothing to report yet, unlike `NotCompleted`, which says the customer has not even started.
 */
const BIOMETRIC_STATEMENT: Record<TBiometricConfidenceLevel, string> = {
  NotCompleted: 'Your customer has not completed their biometric check.',
  InProgress: 'No outcome is available yet.',
  Low: 'Low level of confidence that the person who completed the check is the person shown on the identity document.',
  High: 'High level of confidence that the person who completed the check is the person shown on the identity document.',
};

/**
 * What a check card says when the data behind it could not be loaded.
 *
 * **Shared by both axes**, unlike every other string here, because a failed request is not a
 * property of the axis. The document and biometric cards fail the same way and there is nothing
 * axis-specific to say about it.
 *
 * Deliberately shaped as a name plus prose, so the error renders through the card's existing bold
 * lead and paragraph rather than a region of its own. That keeps the card the same height and shape
 * whichever state it is in, which is what stops a page of cards reflowing as calls land.
 *
 * **Not a level.** `Not available` is never returned by the API and is not a member of either level
 * type. It exists only for this state, and it is worded to say the check could not be *shown*, not
 * that the check is in some new outcome. The distinction matters: `NotCompleted` means the customer
 * has not done the check, which is a fact about the customer, while this means we could not find
 * out, which is a fact about us.
 */
export const UNAVAILABLE_LEVEL_NAME = 'Not available';

/**
 * The prose that follows `UNAVAILABLE_LEVEL_NAME`. Approved by Charles on 7 August.
 *
 * It says what happened and what to do, and nothing else. An earlier draft added "so no level can be
 * shown", which restated what the reader can already see: the level is missing from the card in
 * front of them.
 */
export const UNAVAILABLE_STATEMENT =
  'This check could not be loaded. Refresh the page to try again.';

/** The statement prose that follows a document level's bold name on a check card. */
export function documentConfidenceStatement(level: TDocumentConfidenceLevel): string {
  return DOCUMENT_STATEMENT[level];
}

/** The statement prose that follows a biometric level's bold name on a check card. */
export function biometricConfidenceStatement(level: TBiometricConfidenceLevel): string {
  return BIOMETRIC_STATEMENT[level];
}
